<template>
  <teleport to="body">
    <div :class="fadeTransition" class="modal">
      <div :class="bounceTransition" class="modal-body">
        <div class="modal-header">
          <h1>{{ title }}</h1>
          <div style="height: 30px; width: 30px" v-on:click="closeModal">
            <CloseSVG :propId="'save-label-close'" />
          </div>
        </div>
        <form v-on:submit.prevent>
          <div class="form-group">
            We'll save your design for you, so you can order today, tomorrow, or even next year, and
            all your hard work will be safe with us.
          </div>
          <div class="form-group">
            <label for="save-artwork">Design Name</label>
            <input id="save-artwork" class="form-input" v-model="artworkName" required />
          </div>
          <div
            :style="{
              visibility: userArtworkFetch.artwork && !saveAsNew ? undefined : 'hidden',
              color: '#0dc5e1',
              fontWeight: 700,
            }"
            class="form-group"
          >
            <span @click="saveAsNew = true">Save as New Design</span>
          </div>
          <button
            v-if="!loading"
            aria-label="Submit save label"
            @click="handleSaveArtwork"
            class="modal-select-btn"
            type="submit"
          >Save</button>
          <div v-else id="loading-wrap">
            <img src="@/assets/loader.svg" />
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, computed, onMounted, inject } from "vue";
import axios from "axios";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import { setCanvasJson } from "@/canvas-api/ConvertCanvas";
import { prepareArtwork } from "@/composables/usePrepareSaveArtwork";
import { getEditorUrlParam } from "@/composables/useParseURL";
import usePrintPreview from "@/composables/usePrintPreview";
import { ActionTypes } from "@/store/actions";
import { CurrentLabel } from "@/types";

export default defineComponent({
  name: "SaveLabel",
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const editorParams = computed(() => store.getters.editorParams);
    const userArtworkFetch = computed(() => store.getters.userArtworkFetch);
    const currentLabel = computed<CurrentLabel>(() => store.getters.currentLabel);
    const currentLabelMaterialId = computed<CurrentLabel>(
      () => store.getters.currentLabelMaterialId
    );
    const currentLabelType = computed<string>(() => store.getters.currentLabelType);

    const isRotated = computed<boolean>(() =>
      store.getters.canvasOrientation === "rotated" ? true : false
    );
    const getDefaultOrientation = computed<string>(() =>
      store.getters.currentLabel.width >= store.getters.currentLabel.height
        ? "landscape"
        : "portrait"
    );
    const currentOrientation = computed<string>(() =>
      isRotated.value
        ? getDefaultOrientation.value === "landscape"
          ? "portrait"
          : "landscape"
        : getDefaultOrientation.value
    );

    const title = ref("");
    const saveAsNew = ref(false);
    const artworkName = ref("Blank");
    onMounted(() => {
      if (userArtworkFetch.value.artwork && Object.keys(userArtworkFetch.value.artwork).length) {
        artworkName.value = userArtworkFetch.value.artwork.title;
      }

      //Trigger rerender on mount
      title.value = "Save Your Design";
    });

    const loading = ref(false);
    const handleSaveArtwork = async () => {
      loading.value = true;
      const labelGroup = await usePrintPreview();
      const canvasAsJSON = await setCanvasJson();

      if (labelGroup && canvasAsJSON) {
        const svg = await prepareArtwork(labelGroup, isRotated.value);

        try {
          // prettier-ignore
          const data = {
            svg,
            id: null,
            title: artworkName.value,
            width: currentLabel.value.width,
            height: currentLabel.value.shape_id === 10 || currentLabel.value.shape_id === 3 ? currentLabel.value.width : currentLabel.value.height,
            shape_id: currentLabel.value.shape_id,
            label_id: currentLabel.value.label_id,
            corners: currentLabel.value.corners,
            corner_radius: currentLabel.value.corner_radius,
            editor: "full",
            editorvue: "1",
            blank_editor: "0",
            material_id: currentLabelMaterialId.value ? currentLabelMaterialId.value : getEditorUrlParam("material_id").length ? getEditorUrlParam("material_id") : "1",
            rush_id: getEditorUrlParam("rush_id").length ? getEditorUrlParam("rush_id") : "4",
            product_id: editorParams.value.product_id,
            orientation: currentOrientation.value,
            json: canvasAsJSON,
          };

          if (userArtworkFetch.value.artwork && !saveAsNew.value) {
            data.id = userArtworkFetch.value.artwork.id;
          }

          let res = await axios.post(
            `${process.env.VUE_APP_API_URL}/editor/artworks/ajax.php?action=save`,
            data
          );

          const response = {
            isFetching: false,
            payload: {
              fabric_objects: JSON.parse(canvasAsJSON),
              artwork: res.data,
            },
            error: "",
          };

          if (currentLabelType.value === "printed") {
            const data = res.data;
            if (data.hash && data.material_id && data.label && data.label.name) {
              const url = `${window.location.origin}/label-design/${currentLabelType.value}/${data.label.name}?hash=${data.hash}&material_id=${data.material_id}`;

              history.pushState(undefined, "", url);
            }
          }

          store.dispatch(ActionTypes.SET_USER_ARTWORK_FETCH, response);

          store.dispatch(ActionTypes.SET_MODAL, 'saveConfirmation');

          loading.value = false;
          saveAsNew.value = false;
        } catch (e) {
          console.log(e);

          loading.value = false;
        }
      }
    };

    return {
      userArtworkFetch,
      closeModal,
      fadeTransition,
      bounceTransition,
      title,
      saveAsNew,
      artworkName,
      loading,
      handleSaveArtwork,
    };
  },
});
</script>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  width: 400px;
  height: unset;
  padding: 4rem;
  padding: 25px;
  border-radius: 6px;
  overflow: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.modal-header h1 {
  font-weight: 600;
  font-size: 22px;
  letter-spacing: -0.25px;
}

.close {
  border-radius: 50%;
  border: solid 1px black;
  background-color: white;
  height: 25px;
  width: 25px;
}

form {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.form-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 14px;
  margin-bottom: 2rem;
}

.form-group:first-child {
  margin-top: 2rem;
}

.form-group label {
  margin-bottom: 5px;
  font-size: 16px;
  font-weight: 700;
}

.form-input {
  border-radius: 4px;
  border: 1px solid #ccc;
  height: 25px;
  padding: 6px 12px;
}

.form-input:focus {
  border-color: #66afe9;
  outline: 0;
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%),
    0 0 8px rgb(102 175 233 / 60%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
}

#loading-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-select-btn {
  border: none;
  border-radius: 4px;
  background: #ff6c00;
  color: white;
  font-size: 16px;
  padding: 1.5rem;
  text-decoration: none;
  text-align: center;
  width: 100%;
  text-shadow: none;
}

.modal-select-btn:hover {
  background: linear-gradient(to bottom, #ff994e 0%, #ff6c00 100%);
}

@media (max-width: 767px) {
  .modal-body {
    position: fixed;
  }
}

@media (max-width: 500px) {
  .modal-body {
    width: 95%;
  }
}
</style>
